import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ToastProvider, ToastContainer, BottomNav } from '../components/ui';
import { usePlaylistSyncToasts } from '../hooks';
import styles from './MainLayout.module.css';

/**
 * Component that initializes global app hooks.
 * Must be rendered inside providers that the hooks depend on.
 */
function GlobalHooksInitializer() {
  // Subscribe to playlist sync events for toast notifications
  usePlaylistSyncToasts();
  return null;
}

/**
 * Main application layout with header, content area, and toast notifications.
 * Wraps all pages with consistent navigation and styling.
 */
export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const isSongsPage = location.pathname === '/songs' || location.pathname.startsWith('/songs/');
  const isDiscoverPage = location.pathname === '/discover';
  const isSettingsPage = location.pathname === '/settings';

  return (
    <ToastProvider>
      <GlobalHooksInitializer />
      <div className={styles.layout}>
        {/* Skip link for keyboard navigation */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <header className={styles.header} role="banner">
          <button
            className={styles.logoButton}
            onClick={() => navigate('/songs')}
            aria-label="Go to song list"
          >
            <span className={styles.logoIcon} aria-hidden="true">
              <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="2" />
                <circle cx="16" cy="16" r="6" fill="currentColor" />
                <path
                  d="M16 2C16 2 20 8 20 16C20 24 16 30 16 30"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M16 2C16 2 12 8 12 16C12 24 16 30 16 30"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className={styles.logoText}>Singlist</span>
          </button>

          {/* Desktop Navigation */}
          <nav className={styles.nav} aria-label="Main navigation">
            {/* Desktop nav links - hidden on mobile */}
            <button
              className={`${styles.navLink} ${isSongsPage ? styles.navLinkActive : ''}`}
              onClick={() => navigate('/songs')}
              aria-current={isSongsPage ? 'page' : undefined}
            >
              Songs
            </button>
            <button
              className={`${styles.navLink} ${isDiscoverPage ? styles.navLinkActive : ''}`}
              onClick={() => navigate('/discover')}
              aria-current={isDiscoverPage ? 'page' : undefined}
            >
              Discover
            </button>
            <button
              className={`${styles.navLink} ${isSettingsPage ? styles.navLinkActive : ''}`}
              onClick={() => navigate('/settings')}
              aria-current={isSettingsPage ? 'page' : undefined}
            >
              Settings
            </button>
          </nav>
        </header>

        <main id="main-content" className={styles.content} role="main" tabIndex={-1}>
          <Outlet />
        </main>

        {/* Mobile Bottom Navigation */}
        <BottomNav />

        <ToastContainer />
      </div>
    </ToastProvider>
  );
}
